import { useCallback, useEffect, useState } from "react";
import { api } from "@/api/client";
import { useStore } from "@/state/store";
import { ConfirmDialog } from "@/components/ConfirmDialog";

/**
 * Notfall-Modus — die Antwort auf „was, wenn es nicht funktioniert".
 *
 * Fällt das Gast-WLAN aus oder klemmt die Anthropic-API, schaltet der Moderator
 * hier auf vorbereitete Ergebnisse aus einem früheren Durchlauf um. Die Sitzung
 * läuft weiter, die Besuchergruppe merkt nichts: auf der Leinwand ist der
 * Schalter nirgends zu sehen, nur hier im Admin.
 *
 * Umschalten braucht eine Bestätigung — ein Fehlklick mitten in der Sitzung
 * wäre teuer. Fehler werden angezeigt, nicht geschluckt: wer glaubt, er habe
 * umgeschaltet, obwohl der Aufruf fehlschlug, steht doppelt im Regen.
 */
export function AdminFallbackSection() {
  const { state } = useStore();
  const [enabled, setEnabled] = useState<boolean | null>(null);
  const [preparedCount, setPreparedCount] = useState(0);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirmTarget, setConfirmTarget] = useState<boolean | null>(null);

  const token = state.auth.token;

  const reload = useCallback(async () => {
    try {
      const r = await api.admin.fallback(token);
      setEnabled(r.enabled);
      setPreparedCount(r.preparedCount);
      setError(null);
    } catch (err) {
      console.error(err);
      setError("Konnte den Notfall-Modus nicht laden.");
    }
  }, [token]);

  useEffect(() => {
    reload();
  }, [reload]);

  const apply = async (next: boolean) => {
    setConfirmTarget(null);
    setBusy(true);
    setError(null);
    try {
      const r = await api.admin.setFallback(token, next);
      setEnabled(r.enabled);
      setPreparedCount(r.preparedCount);
    } catch (err) {
      console.error(err);
      setError(
        next
          ? "Umschalten fehlgeschlagen — der Notfall-Modus ist NICHT aktiv."
          : "Abschalten fehlgeschlagen — der Notfall-Modus läuft weiter.",
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="admin-grid" data-testid="admin-fallback">
      <header className="admin-section-head">
        <h2>Notfall-Modus</h2>
        <p>
          Zeigt vorbereitete Ergebnisse aus einem früheren Durchlauf, statt live
          zu generieren. Für den Fall, dass WLAN oder KI-API ausfallen. Auf der
          Leinwand ist davon nichts zu sehen.
        </p>
      </header>

      {error && (
        <div className="admin-callout" role="alert">
          {error}
        </div>
      )}

      {enabled === true && (
        <div className="admin-callout" role="status" data-testid="fallback-active-note">
          Notfall-Modus ist AKTIV — die Leinwand zeigt vorbereitete Ergebnisse.
        </div>
      )}

      <p className="form-hint" data-testid="fallback-prepared-count">
        {preparedCount === 0
          ? "Keine vorbereiteten Ergebnisse hinterlegt — der Schalter bleibt bis zur Generalprobe wirkungslos. Es wird weiter das Echte gezeigt."
          : `${preparedCount} vorbereitete Ergebnisse hinterlegt.`}
      </p>

      <div style={{ display: "flex", gap: 8 }}>
        <button
          type="button"
          className={enabled ? "btn btn-ghost" : "btn btn-primary"}
          disabled={busy || enabled === null}
          onClick={() => setConfirmTarget(!enabled)}
        >
          {enabled
            ? "Notfall-Modus ausschalten"
            : "Notfall-Modus einschalten"}
        </button>
      </div>

      <ConfirmDialog
        open={confirmTarget !== null}
        title={
          confirmTarget
            ? "Notfall-Modus einschalten?"
            : "Notfall-Modus ausschalten?"
        }
        message={
          confirmTarget
            ? "Ab sofort werden vorbereitete Ergebnisse gezeigt statt der live erzeugten. Wo nichts vorbereitet ist, bleibt das echte Ergebnis stehen."
            : "Es werden wieder die live erzeugten Ergebnisse gezeigt."
        }
        confirmLabel={confirmTarget ? "Einschalten" : "Ausschalten"}
        variant={confirmTarget ? "danger" : "primary"}
        busy={busy}
        onConfirm={() => apply(confirmTarget === true)}
        onCancel={() => setConfirmTarget(null)}
      />
    </div>
  );
}

/**
 * Dauerhafter Hinweis über allen Admin-Tabs. Der Moderator darf nie in dem
 * Glauben moderieren, er sehe Live-Ergebnisse, während der Schalter läuft.
 */
export function AdminFallbackBanner() {
  const { state } = useStore();
  const [enabled, setEnabled] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const token = state.auth.token;

  useEffect(() => {
    let cancelled = false;
    const poll = async () => {
      try {
        const r = await api.admin.fallback(token);
        if (!cancelled) setEnabled(r.enabled);
      } catch (err) {
        console.error(err);
      }
    };
    poll();
    const id = window.setInterval(poll, 15_000);
    return () => {
      cancelled = true;
      window.clearInterval(id);
    };
  }, [token]);

  // Ausschalten gehört an den Hinweis, nicht in einen Reiter, den man erst
  // suchen muss. Wer merkt, dass er versehentlich im Notfall-Modus moderiert,
  // will ihn sofort loswerden — nicht nach der Bedienungsanleitung greifen.
  const turnOff = async () => {
    setBusy(true);
    setError(null);
    try {
      const r = await api.admin.setFallback(token, false);
      setEnabled(r.enabled);
    } catch (err) {
      console.error(err);
      setError("Abschalten fehlgeschlagen — der Notfall-Modus läuft weiter.");
    } finally {
      setBusy(false);
    }
  };

  if (!enabled) return null;
  return (
    <div className="admin-callout" role="status" data-testid="fallback-banner">
      <strong>Notfall-Modus läuft.</strong> Die Leinwand zeigt vorbereitete
      Ergebnisse, keine live erzeugten.{" "}
      <button
        type="button"
        className="btn btn-ghost"
        onClick={turnOff}
        disabled={busy}
        data-testid="fallback-banner-off"
      >
        {busy ? "Schalte ab…" : "Jetzt ausschalten"}
      </button>
      {error && <div role="alert">{error}</div>}
    </div>
  );
}
